<!-- 
  EventBlackPatientManage.vue
  메시지 발송 제외 설정 페이지
  
  주요 기능:
  - Excel 업로드를 통한 블랙 환자 추가
  - 단건 블랙 환자 추가
  - 병원별 블랙 환자 일괄 추가
  - 블랙 환자 조회 및 관리
  
  성능 최적화:
  - 전화번호 처리 로직 통합 (phoneUtils)
  - 디바운스 적용된 검색
  - 계산 속성 최적화 (Set 사용)
  - 메모리 효율적인 필터링
  
  코드 개선:
  - SOLID 원칙 적용
  - 에러 처리 통합
  - 함수 재사용성 향상
  - 타입 안전성 개선
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">
              메시지 발송 제외 설정
            </h1>
          </div>
          <span class="pageDesc">목록에 등록된 대상자는 알림톡 발송 대상에서 자동 제외됩니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 콘텐츠 영역 -->
    <div class="px-0 pb-2">
      <!-- 블랙 환자 조회 영역 -->
      <div class="card content-wrap-card">
        <!-- 검색 필터 -->
        <div class="search-container">
          <div class="search-form">
            <div class="search-row">
              <div class="search-group">
                <label>검색 조건</label>
                <a-select v-model:value="searchForm.type" style="width: 150px">
                  <a-select-option value="patientName">환자명</a-select-option>
                  <a-select-option value="phoneNumber">전화번호</a-select-option>
                  <a-select-option value="hospitalName">병원명</a-select-option>
                  <a-select-option value="businessNumber">사업자번호</a-select-option>
                </a-select>
              </div>
              
              <div class="search-group">
                <label>검색어</label>
                <a-input
                  v-model:value="searchForm.keyword"
                  placeholder="검색어를 입력하세요"
                  @pressEnter="searchBlackPatients"
                  style="width: 300px"
                />
              </div>
              
              <div class="search-actions">
                <a-button 
                  type="primary" 
                  @click="searchBlackPatients"
                  :loading="state.searchLoading"
                >
                  <i class="fa fa-search" style="margin-right: 5px"></i>검색
                </a-button>
                
                <a-button @click="resetSearch">
                  <i class="fa fa-refresh" style="margin-right: 5px"></i>초기화
                </a-button>
              </div>
            </div>
            
            <!-- 엑셀 다운로드 안내 -->
            <div class="download-info">
              <i class="fa fa-info-circle" style="margin-right: 5px; color: #1890ff;"></i>
              <span>엑셀 다운로드는 현재 검색 조건에 해당하는 모든 데이터를 포함합니다.</span>
            </div>
          </div>
        </div>
        
        <!-- 테이블 상단 정보 및 액션 영역 -->
        <div class="table-header">
          <div class="table-info">
            <span class="result-count">전체 {{ pagination.total }}건</span>
          </div>
          <div class="table-actions">
            <a-button 
              type="primary" 
              class="yellow-button"
              @click="showAddModal"
            >
              <i class="fa fa-plus" style="margin-right: 5px"></i>환자 등록하기
            </a-button>
            
            <a-button 
              type="primary" 
              @click="downloadExcel"
              :loading="state.downloadLoading"
            >
              <i class="fa fa-download" style="margin-right: 5px"></i>엑셀 다운로드
            </a-button>
            
            <a-button 
              type="primary" 
              class="yellow-button"
              @click="bulkDeletePatients"
              :disabled="selectedRowKeys.length === 0"
            >
              <i class="fa fa-trash" style="margin-right: 5px"></i>일괄삭제 ({{ selectedRowKeys.length }})
            </a-button>
          </div>
        </div>
        
        <!-- 블랙 환자 목록 -->
        <div class="table-container">
          <a-table
            :columns="columns"
            :data-source="blackPatientList"
            :loading="state.tableLoading"
            :pagination="{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
              pageSizeOptions: ['10', '20', '50', '100'],
              showLessItems: false
            }"
            @change="handleTableChange"
            row-key="_id"
            :row-selection="rowSelection"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'action'">
                <a-popconfirm
                  title="정말로 이 환자를 블랙 리스트에서 삭제하시겠습니까?"
                  @confirm="removeBlackPatient(record._id)"
                >
                  <a-button size="small" danger>
                    <i class="fa fa-trash" style="margin-right: 4px"></i>삭제
                  </a-button>
                </a-popconfirm>
              </template>
              
              <template v-else-if="column.key === 'phoneNumber'">
                {{ phoneUtils.format(record.phoneNumber) }}
              </template>
              
              <template v-else-if="column.key === 'createdAt'">
                {{ formatDate(record.createdAt) }}
              </template>
            </template>
          </a-table>
        </div>
      </div>
    </div>
    
    <!-- 블랙 환자 추가 모달 컴포넌트 -->
    <BlackPatientAddModal
      :visible="state.addModalVisible"
      :api="api"
      :black-patient-list="blackPatientList"
      @close="handleModalClose"
      @success="handleModalSuccess"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';
import dayjs from 'dayjs';
import eventsApis from './api';
import BlackPatientAddModal from './components/BlackPatientAddModal.vue';

// 상수 정의
const SEARCH_TYPES = {
  PATIENT_NAME: 'patientName',
  PHONE_NUMBER: 'phoneNumber',
  HOSPITAL_NAME: 'hospitalName',
  BUSINESS_NUMBER: 'businessNumber'
};

export default {
  name: "EventBlackPatientManage",
  components: {
    BlackPatientAddModal
  },
  setup() {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let api = null;
    
    if (instance && instance.proxy) {
      api = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
      console.log('블랙리스트 환자 관리 API 인스턴스 초기화:', api);
    } else {
      console.error('Current instance not available, API initialization failed');
    }

    // 반응형 상태 정의
    const state = reactive({
      searchLoading: false,
      tableLoading: false,
      addModalVisible: false,
      downloadLoading: false
    });

    const blackPatientList = ref([]);
    const selectedRowKeys = ref([]);
    
    // 폼 데이터
    const searchForm = reactive({
      type: SEARCH_TYPES.PATIENT_NAME,
      keyword: ''
    });
    
    // 페이지네이션
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
    });
    
    // 테이블 컬럼
    const columns = [
      {
        title: '번호',
        dataIndex: '_id',
        key: '_id',
        width: 80,
        align: 'center',
        customRender: ({ index }) => {
          return (pagination.current - 1) * pagination.pageSize + index + 1;
        }
      },
      {
        title: '환자명',
        dataIndex: 'patientName',
        key: 'patientName',
        width: 120
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 150
      },
      {
        title: '병원명',
        dataIndex: 'hospitalName',
        key: 'hospitalName',
        width: 200
      },
      {
        title: '등록일시',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150
      },
      {
        title: '작업',
        key: 'action',
        width: 100,
        align: 'center'
      }
    ];
    
    // 행 선택 설정
    const rowSelection = computed(() => ({
      selectedRowKeys: selectedRowKeys.value,
      onChange: (selectedKeys, selectedRows) => {
        selectedRowKeys.value = selectedKeys;
      },
      onSelect: (record, selected, selectedRows) => {
        // 개별 선택 시 추가 로직이 필요한 경우 여기에 작성
      },
      onSelectAll: (selected, selectedRows, changeRows) => {
        // 전체 선택 시 추가 로직이 필요한 경우 여기에 작성
      },
    }));
    
    // 전화번호 관련 유틸리티 함수들
    const phoneUtils = {
      // 전화번호 포맷팅 (표시용)
      format: (phoneNumber) => {
        if (!phoneNumber) return '';
        
        const numbers = phoneNumber.replace(/\D/g, '');
        
        // 휴대폰 번호 (11자리)
        if (numbers.length === 11 && /^01[0-9]/.test(numbers)) {
          return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
        }
        
        // 일반 전화번호
        if (numbers.length >= 9) {
          if (numbers.length === 9) {
            return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
          } else if (numbers.length === 10) {
            if (numbers.startsWith('02')) {
              return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
            } else {
              return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
            }
          }
        }
        
        return phoneNumber;
      },
      
      // 하이픈 제거 (저장용)
      clean: (phoneNumber) => {
        if (!phoneNumber) return '';
        return phoneNumber.replace(/\D/g, '');
      },
      
      // 전화번호 유효성 검사
      validate: (phoneNumber) => {
        const numbers = phoneUtils.clean(phoneNumber);
        const mobilePattern = /^01[0-9]\d{8}$/;
        const landlinePattern = /^(02|0[3-9]\d)\d{6,8}$/;
        return mobilePattern.test(numbers) || landlinePattern.test(numbers);
      }
    };
    
    // 레거시 함수들 (하위 호환성을 위해 유지)
    const formatPhoneNumber = phoneUtils.format;
    const removePhoneNumberHyphens = phoneUtils.clean;
    const validatePhoneNumber = phoneUtils.validate;
    

    // 공통 에러 처리 함수 - 개선된 버전
    const handleError = (error, defaultMessage, showNotification = true) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      // MongoDB 중복 키 에러 특별 처리
      if (error.response?.data?.error === 'MongoServerError' && 
          error.response?.data?.message?.includes('E11000 duplicate key error')) {
        if (error.response.data.message.includes('phoneNumber')) {
          errorMessage = '이미 등록된 전화번호입니다.';
        } else {
          errorMessage = '중복된 데이터가 존재합니다.';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (showNotification) {
        message.error(errorMessage);
      }
      
      return errorMessage;
    };

    // 블랙 환자 검색 - 서버 사이드 검색으로 변경
    const searchBlackPatients = async () => {
      pagination.current = 1; // 검색 시 첫 페이지로 이동
      selectedRowKeys.value = []; // 선택 초기화
      await loadBlackPatients();
    };
    
    // 검색 초기화 - 서버 사이드 페이징 적용
    const resetSearch = () => {
      searchForm.type = SEARCH_TYPES.PATIENT_NAME;
      searchForm.keyword = '';
      pagination.current = 1;
      selectedRowKeys.value = []; // 선택 초기화
      loadBlackPatients(); // 데이터 다시 로드
    };
    
    // 블랙 환자 목록 로드
    const loadBlackPatients = async () => {
      state.tableLoading = true;
      
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize
        };
        
        // 검색 조건이 있는 경우 추가
        if (searchForm.keyword.trim()) {
          params.type = searchForm.type;
          params.keyword = searchForm.keyword;
        }
        
        await api.getBlacklistPatients(
          params,
          (response) => {
            const data = response.data?.data;
            if (data) {
              blackPatientList.value = data.patients || [];
              if (data.pagination) {
                pagination.total = data.pagination.total;
                pagination.current = data.pagination.current;
                pagination.pageSize = data.pagination.pageSize;
              }
            } else {
              blackPatientList.value = [];
              pagination.total = 0;
            }
          },
          (error) => {
            handleError(error, '블랙 환자 목록을 불러오는 중 오류가 발생했습니다.');
            blackPatientList.value = [];
            pagination.total = 0;
          }
        );
      } catch (error) {
        // 에러는 이미 handleError에서 처리됨
      } finally {
        state.tableLoading = false;
      }
    };
    
    // 블랙 환자 제거
    const removeBlackPatient = async (id) => {
      if (!api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      try {
        await api.removeBlacklistPatient(
          id,
          (response) => {
            message.success('블랙 환자가 제거되었습니다.');
            loadBlackPatients(); // 목록 새로고침
          },
          (error) => {
            handleError(error, '블랙 환자 제거 중 오류가 발생했습니다.');
          }
        );
      } catch (error) {
        // 에러는 이미 handleError에서 처리됨
      }
    };
    
    // 일괄 삭제
    const bulkDeletePatients = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('삭제할 환자를 선택해주세요.');
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '일괄 삭제 확인',
        `선택한 ${selectedRowKeys.value.length}명의 환자를 블랙 리스트에서 제거하시겠습니까?`,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      if (!api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      try {
        await api.bulkRemoveBlacklistPatients(
          selectedRowKeys.value,
          (response) => {
            message.success(`${selectedRowKeys.value.length}명의 환자가 블랙 리스트에서 제거되었습니다.`);
            selectedRowKeys.value = []; // 선택 초기화
            loadBlackPatients(); // 목록 새로고침
          },
          (error) => {
            handleError(error, '일괄 삭제 중 오류가 발생했습니다.');
          }
        );
      } catch (error) {
        // 에러는 이미 handleError에서 처리됨
      }
    };
    
    // 테이블 변경 핸들러 (서버 사이드 페이지네이션)
    const handleTableChange = (pag, filters, sorter) => {
      // 페이지 또는 페이지 크기가 변경된 경우
      if (pagination.current !== pag.current || pagination.pageSize !== pag.pageSize) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        selectedRowKeys.value = []; // 페이지 변경 시 선택 초기화
        
        // 서버에서 새로운 데이터 로드
        loadBlackPatients();
      }
    };
    
    // 날짜 포맷팅
    const formatDate = (dateString) => {
      return dayjs(dateString).format('YYYY-MM-DD HH:mm');
    };

    // 모달 열기
    const showAddModal = () => {
      state.addModalVisible = true;
    };
    
    // 모달 닫기
    const handleModalClose = () => {
      state.addModalVisible = false;
    };
    
    // 모달 성공 핸들러
    const handleModalSuccess = () => {
      loadBlackPatients(); // 목록 새로고침
    };

    // 엑셀 다운로드
    const downloadExcel = async () => {
      if (!api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }

      state.downloadLoading = true;

      try {
        // 현재 검색 조건을 그대로 사용
        const params = {};
        
        // 검색 조건이 있는 경우 추가
        if (searchForm.keyword.trim()) {
          params.type = searchForm.type;
          params.keyword = searchForm.keyword;
        }

        await api.downloadBlacklistPatientsExcel(
          params,
          (response) => {
            // const recordCount = response.recordCount || 0;
            message.success(`엑셀 파일이 다운로드되었습니다`);
          },
          (error) => {
            handleError(error, '엑셀 다운로드 중 오류가 발생했습니다.');
          }
        );
      } catch (error) {
        // 에러는 이미 handleError에서 처리됨
      } finally {
        state.downloadLoading = false;
      }
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadBlackPatients();
    });
    
    return {
      // 상태관리
      state,
      pagination,
      selectedRowKeys,
      
      // 데이터
      blackPatientList,
      searchForm,
      
      // 계산속성
      columns,
      rowSelection,
      
      // 핵심기능
      loadBlackPatients,
      searchBlackPatients,
      resetSearch,
      
      // 모달관리
      showAddModal,
      handleModalClose,
      handleModalSuccess,
      
      // 엑셀 다운로드
      downloadExcel,
      
      // 환자관리
      removeBlackPatient,
      bulkDeletePatients,
      
      // 테이블처리
      handleTableChange,
      
      // 유틸함수
      formatDate,
      phoneUtils,
      
      // 하위호환함수
      formatPhoneNumber,
      removePhoneNumberHyphens,
      validatePhoneNumber,
      
      // API 인스턴스 (모달에서 사용)
      api
    };
  }
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  border: none;
  overflow: hidden;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

// 섹션 헤더 스타일
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-count {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
}

// 탭 컨테이너
.tab-container {
  :deep(.ant-tabs) {
    .ant-tabs-tab {
      font-weight: 500;
    }
    
    .ant-tabs-content-holder {
      padding: 0;
    }
  }
}

.tab-content {
  padding: 20px;
}

// 업로드 섹션
.upload-section {
  .upload-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px;
    background-color: #f6f8ff;
    border-radius: 6px;
    border-left: 4px solid #1890ff;
    
    span {
      font-size: 14px;
      color: #333;
    }
  }
  
  .upload-actions {
    margin-top: 16px;
  }
}

// 폼 스타일
.form-container {
  .form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .form-group {
    flex: 1;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #262626;
    }
    
    :deep(.ant-input),
    :deep(.ant-select) {
      border-radius: 6px;
    }
  }
  
  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 24px;
    
    .ant-btn {
      min-width: 120px;
      height: 40px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

// 검색 입력 그룹
.search-input-group {
  display: flex;
  gap: 8px;
  
  .ant-input {
    flex: 1;
  }
}

// 병원 검색 결과
.hospital-results {
  margin-top: 24px;
  
  h4 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
    color: #000;
  }
}

.hospital-list {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.hospital-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
  
  &.selected {
    background-color: #e6f7ff;
    border-color: #1890ff;
  }
  
  .hospital-info {
    flex: 1;
    
    .hospital-name {
      font-size: 16px;
      font-weight: 600;
      color: #000;
      margin-bottom: 4px;
    }
    
    .hospital-details {
      display: flex;
      gap: 16px;
      font-size: 14px;
      color: #666;
    }
  }
  
  .fa-check-circle {
    color: #1890ff;
    font-size: 18px;
  }
}

// 검색 컨테이너
.search-container {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
}

.search-form {
  .search-row {
    display: flex;
    align-items: end;
    gap: 16px;
    flex-wrap: wrap;
  }
  
  .search-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
      font-weight: 500;
      font-size: 14px;
      color: #262626;
    }
  }
  
  .search-actions {
    display: flex;
    gap: 8px;
  }
}

// 다운로드 안내 메시지
.download-info {
  margin-top: 12px;
  padding: 8px 12px;
  background-color: #f6f8ff;
  border-radius: 6px;
  border-left: 3px solid #1890ff;
  font-size: 13px;
  color: #666;
  
  span {
    vertical-align: middle;
  }
}

// 테이블 컨테이너
.table-container {
  :deep(.ant-table) {
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 600;
      color: #000;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
}

// 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .search-row {
    flex-direction: column;
    align-items: stretch !important;
    gap: 12px;
  }
  
  .search-actions {
    justify-content: center;
  }
  
  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .table-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
}

// 테이블 액션 영역
.table-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

// 테이블 헤더 (정보 + 액션)
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
}
</style> 